import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import axios from 'axios';
import { v1 as uuid } from 'uuid';
import {
    Button, Dialog, DialogContent,
    DialogActions, makeStyles,
    Grid, Tooltip,
} from '@material-ui/core';
import { useApolloClient } from '@apollo/client';
import { TRAINING_ELEMENT } from 'utils/enum/General';
import { Col, Form } from 'react-bootstrap';
import { FetchPolicy } from 'utils/enum/Core';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import ButtonStyles from 'styles/theme/Button';
import Select from 'components/widgets/Select';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import GeneralQuery from 'services/graphQL/query/GeneralQuery';

// icons
import HelpOutlinedIcon from '@material-ui/icons/HelpOutlined';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
        },
    },
    content: {
        padding: '20px 10px',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    dialogActions: {
        '& > .form-group': {
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '40px',
            [theme.breakpoints.down('sm')]: {
                paddingRight: 0,
            },
        },
    },
    subLabel: {
        fontSize: '13px',
        display: 'flex',
        '& > svg': {
            width: '15px',
            height: '15px',
            cursor: 'pointer',
            marginLeft: '5px',
        },
    },
    input: {
        fontSize: '13px',
        resize: 'none',
    },
    videoLinkHelp: {
        fontSize: '12px',
        fontWeight: 500,
    },
    uploader: {
        display: 'none',
    },
    uploaderParent: {
        marginLeft: '15px',
        marginRight: '15px',
        marginBottom: '15px',
        '& > button': {
            width: '100%',
        },
    },
}));

const defaultVideoPrefix = 'https://player.vimeo.com/video/';
const VideoDialog = ({
    toggleDialog,
    onSave,
    isSavingElement,
    categories,
    selectedCategory,
    selectedElement,
    hasGlobalPermission,
}) => {
    const client = useApolloClient();
    const uploader = useRef(null);
    const classes = { ...useStyles(), ...buttonStyles() };
    const currentCategory = categories.find((cat) => cat.trainingCategoryId === selectedCategory);
    const currentElement = (currentCategory?.elements ?? []).find((v) => v.trainingElementId === selectedElement);

    const [state, setState] = useState({
        elementCategory: currentCategory?.trainingCategoryId,
        elementName: currentElement?.name,
        elementDescription: currentElement?.description,
        elementType: currentElement?.type ?? TRAINING_ELEMENT.VIDEO,
        elementURL: currentElement?.url ?? defaultVideoPrefix,
        matchingRoute: currentElement?.matchingRoute,
        isUploadingFile: false,
    });

    const {
        elementCategory,
        elementName,
        elementDescription,
        elementType,
        elementURL,
        matchingRoute,
        isUploadingFile,
    } = state;

    const onChange = (name, value) => {
        const isType = name === 'elementType';

        setState((prevState) => ({
            ...prevState,
            [name]: value,
            ...(isType ? {
                elementURL: value === TRAINING_ELEMENT.VIDEO ? defaultVideoPrefix : '',
            } : {}),
        }));
    };

    const openFileDialog = () => {
        const { current } = uploader;
        if (current) current.click();
    };

    const onImageUpload = async ({ target }) => {
        const file = target.files[0];
        const { size, name } = file;
        const extension = name.substr(name.lastIndexOf('.'))?.toLowerCase();

        // eslint-disable-next-line no-param-reassign
        target.value = null;

        const maxSizeAllowed = 10485760; // 10MB in bytes
        if (size > maxSizeAllowed) {
            ModalUtils.errorMessage(null, 'Maximum file size allowed is 10MB.');
            return;
        }

        if (extension?.toLowerCase() !== '.pdf') {
            ModalUtils.errorMessage(null, 'You can only upload PDF files.');
            return;
        }

        setState((prevState) => ({
            ...prevState,
            isUploadingFile: true,
        }));

        try {
            const { data } = await client.query({
                query: GeneralQuery.GET_TRAINING_DOCUMENT_UPLOAD_SIGNED_URL,
                variables: {
                    fileName: `${uuid()}${extension.toLowerCase()}`,
                },
                fetchPolicy: FetchPolicy.NO_CACHE,
            });

            const { url, path } = data.getTrainingDocumentUploadSignedURL;
            const { status } = await axios.put(url, file);

            if (status !== 200) throw new Error('File could not be uploaded');
            setState((prevState) => ({
                ...prevState,
                elementURL: path,
                isUploadingFile: false,
            }));

            ModalUtils.successMessage(null, 'File uploaded successfully');
        } catch (error) {
            ModalUtils.errorMessage(null, error.message);
            setState((prevState) => ({
                ...prevState,
                isUploadingFile: false,
            }));
        }
    };

    const isValidURL = elementType !== TRAINING_ELEMENT.VIDEO || (!StringUtils.isEmpty(elementURL)
        && StringUtils.isSecureURL(elementURL)
        && (
            elementURL.toLowerCase().includes('player.vimeo.com')
            || elementURL.toLowerCase().includes('youtube.com/embed')
        ));
    const isValidRoute = StringUtils.isEmpty(matchingRoute) || (!StringUtils.isEmpty(matchingRoute) && StringUtils.isSecureURL(matchingRoute));
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            disableEscapeKeyDown
            scroll="paper"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <DialogAppBar
                appBarClassName={classes.AppBar}
                title="Resource"
                onClose={() => toggleDialog()}
                toolbarSize="md"
            />
            <DialogContent>
                <div className={classes.content}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>Category</Form.Label>
                                <Select
                                    nowrap
                                    size="sm"
                                    loading={false}
                                    className={classes.input}
                                    name="elementCategory"
                                    onChange={(name, value) => onChange(name, value)}
                                    value={elementCategory || ''}
                                    options={categories.map((c) => ({ label: c.name, value: c.trainingCategoryId }))}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>Name</Form.Label>
                                <Form.Control
                                    autoFocus
                                    className={clsx(classes.input, StringUtils.isEmpty(elementName) ? 'invalid-field' : '')}
                                    spellCheck="false"
                                    type="text"
                                    name="elementName"
                                    value={elementName || ''}
                                    onChange={({ target }) => onChange(target.name, target.value)}
                                    maxLength={255}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>Description (optional)</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    spellCheck="false"
                                    as="textarea"
                                    size="sm"
                                    rows="3"
                                    name="elementDescription"
                                    value={elementDescription || ''}
                                    onChange={({ target }) => onChange(target.name, target.value)}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>Type</Form.Label>
                                <Select
                                    nowrap
                                    size="sm"
                                    loading={false}
                                    className={classes.input}
                                    name="elementType"
                                    onChange={(name, value) => onChange(name, value)}
                                    value={elementType || ''}
                                    options={[TRAINING_ELEMENT.VIDEO, TRAINING_ELEMENT.DOCUMENT].map((r) => ({ label: r, value: r }))}
                                />
                            </Form.Group>
                            {elementType === TRAINING_ELEMENT.VIDEO && (
                                <Form.Group as={Col}>
                                    <Form.Label className={classes.subLabel}>
                                        Resource URL
                                        <Tooltip
                                            title={`
                                                Only vimeo and youtube videos supported. Youtube videos must start with https://www.youtube.com/embed/ + videoId
                                                and Vimeo videos with https://player.vimeo.com/video/ + videoId
                                            `}
                                            placement="top"
                                        >
                                            <HelpOutlinedIcon />
                                        </Tooltip>
                                    </Form.Label>
                                    <Form.Control
                                        className={clsx(classes.input, !isValidURL ? 'invalid-field' : '')}
                                        spellCheck="false"
                                        type="text"
                                        name="elementURL"
                                        value={elementURL || ''}
                                        onChange={({ target }) => onChange(target.name, target.value)}
                                        maxLength={255}
                                    />
                                    <div className={classes.videoLinkHelp}>https://www.youtube.com/embed/ | https://player.vimeo.com/video/</div>
                                </Form.Group>
                            )}
                            {elementType === TRAINING_ELEMENT.DOCUMENT && (
                                <div className={classes.uploaderParent}>
                                    <Button
                                        disabled={isUploadingFile}
                                        className={!StringUtils.isEmpty(elementURL) ? classes.containedGreen : classes.containedInfo}
                                        size="small"
                                        onClick={openFileDialog}
                                    >
                                        Upload
                                    </Button>
                                    <input
                                        ref={uploader}
                                        className={classes.uploader}
                                        type="file"
                                        onChange={onImageUpload}
                                    />
                                </div>
                            )}
                            {hasGlobalPermission && (
                                <Form.Group as={Col}>
                                    <Form.Label className={classes.subLabel}>
                                        DMS URL (optional)
                                        <Tooltip
                                            title={`
                                                It represents the address at the top bar of the browser to help match a specific video. 
                                                It must start with https://. For detail-like URLs please use {*}. Example: 
https://dmspro.automatrix.com/inventory/140144#advertising -> https://dmspro.automatrix.com/inventory/{*}#advertising
                                            `}
                                            placement="top"
                                        >
                                            <HelpOutlinedIcon />
                                        </Tooltip>
                                    </Form.Label>
                                    <Form.Control
                                        className={
                                            clsx(
                                                classes.input,
                                                !isValidRoute ? 'invalid-field' : '',
                                            )
                                        }
                                        spellCheck="false"
                                        type="text"
                                        name="matchingRoute"
                                        value={matchingRoute || ''}
                                        onChange={({ target }) => onChange(target.name, target.value)}
                                        maxLength={255}
                                    />
                                    <div className={classes.videoLinkHelp}>{'{*}'}</div>
                                </Form.Group>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Form.Group as={Col}>
                    <Button
                        size="small"
                        className={classes.containedSecondaryInfo}
                        disabled={
                            StringUtils.isEmpty(elementName)
                            || StringUtils.isEmpty(elementURL)
                            || !isValidURL
                            || !isValidRoute
                        }
                        onClick={() => onSave(state)}
                    >
                        {isSavingElement ? 'Saving...' : 'Save'}
                    </Button>
                </Form.Group>
            </DialogActions>
        </Dialog>
    );
};

VideoDialog.defaultProps = {
    categories: [],
    selectedCategory: null,
    selectedElement: null,
};

VideoDialog.propTypes = {
    toggleDialog: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isSavingElement: PropTypes.bool.isRequired,
    categories: PropTypes.array,
    selectedCategory: PropTypes.string,
    selectedElement: PropTypes.string,
    hasGlobalPermission: PropTypes.bool.isRequired,
};

export default VideoDialog;
